import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import { ICON } from '@core/util/icon';
import { ClientType, Client } from '@models/clients';
import { ActingForType } from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import { Matter, MatterFileType } from '@models/matter';
import { UrlResult, UrlService } from '@services/url/url.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { CalendarService } from 'src/app/private/calendar/calendar.service';
import { MultipurposeNavigationService } from '@services/nav/multipurpose-navigation.service';

@Component({
	selector: 'app-matter-preview',
	templateUrl: './matter-preview.component.html',
	styleUrls: ['./matter-preview.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MatterPreviewComponent implements OnInit {
	ICON = ICON;

	clients$!: Observable<Client[]>;

	labels$!: Observable<string[]>;

	matter$ = new BehaviorSubject<Matter>(null);

	matterUrl$!: Observable<UrlResult>;
	matterOverviewUrl$!: Observable<UrlResult>;
	matterPropertyUrl$!: Observable<UrlResult>;
	matterPartiesUrl$!: Observable<UrlResult>;
	matterOtherPartiesUrl$!: Observable<UrlResult>;

	otherPartiesClerk$!: Observable<Client>;

	otherPartiesLawyer$!: Observable<Client>;

	otherPartiesFirm$!: Observable<LawFirm>;

	matterId: string;

	@Input() set matter(matter: Matter) {
		this.matter$.next(matter);
		this.matterId = matter.id;
	}

	constructor(
		private calendarService: CalendarService,
		private multipurposeNavigationService: MultipurposeNavigationService,
	) {}

	ngOnInit(): void {
		this.matterUrl$ = this.matter$.pipe(
			map((matter) => UrlService.Matter(matter?.id)),
		);

		this.clients$ = this.matter$.pipe(
			switchMap((matter) =>
				this.calendarService.matterClientsByMatterId(matter.id),
			),
			shareReplay(1),
		);

		this.otherPartiesFirm$ = this.matter$.pipe(
			map((matter) => matter?.otherPartyLawFirm),
			shareReplay(1),
		);

		this.otherPartiesLawyer$ = this.otherPartiesFirm$.pipe(
			map((lawFirm) => lawFirm?.lawyer),
			map((emp) =>
				emp ? ({ ...emp, type: ClientType.PERSON } as unknown as Client) : null,
			),
			shareReplay(1),
		);

		this.otherPartiesClerk$ = this.otherPartiesFirm$.pipe(
			map((lawFirm) => lawFirm?.clerk),
			map((emp) =>
				emp ? ({ ...emp, type: ClientType.PERSON } as unknown as Client) : null,
			),
			shareReplay(1),
		);

		this.labels$ = this.matter$.pipe(
			filter((matter) => !!matter?.propertyDetails?.propertyType),
			map((matter) => matter.propertyDetails?.propertyType),
			map(({ usage, status, condoType, tenanted }) =>
				[
					usage,
					status,
					condoType ? 'condo' : '',
					tenanted ? 'tenanted' : 'non-tenanted',
				].filter((x) => !!x),
			),
		);

		this.matterOverviewUrl$ = this.matter$.pipe(
			map((matter) => UrlService.Matter_Overview(matter.id)),
			shareReplay(1),
		);

		this.matterPropertyUrl$ = this.matter$.pipe(
			map((matter) => UrlService.Matter_Property(matter.id)),
			shareReplay(1),
		);

		this.matterPartiesUrl$ = this.matter$.pipe(
			map((matter) => {
				if (matter.fileType === MatterFileType.REFINANCE) {
					return UrlService.Matter_Parties_Mortgagors(matter.id);
				}
				return UrlService.Matter_Parties(matter.id);
			}),
			shareReplay(1),
		);

		this.matterOtherPartiesUrl$ = this.matter$.pipe(
			map((matter) => {
				if (matter.fileType === MatterFileType.REFINANCE) {
					if (matter.actingFor === ActingForType.MORTGAGEE) {
						return UrlService.Matter_Parties_Mortgagors(matter.id);
					}
					return null;
				}
				return UrlService.Matter_Parties_OtherParties(matter.id);
			}),
			shareReplay(1),
		);
	}

	async goToMatter(event) {
		event.preventDefault();
		await this.multipurposeNavigationService.goToMatter(this.matterId);
	}
}
